import React, { useState, useMemo } from "react";
import { patternLinesDef } from "@nivo/core";
import { ResponsiveLine } from "@nivo/line";
import { ResponsiveBar } from "@nivo/bar";
import classnames from "classnames";
import unique from "array-unique";
import proj4 from "proj4";

import Select from "react-select";
import InputRange from "react-input-range";
import "../components/Range.css";
import Radio from "../assets/radio.svg";

import css from "./Home.css";
import csv from "../assets/All_places.csv";
import actual from "../assets/actual.csv";
import predictions from "../assets/predictions.csv";
import map from "../assets/map.png?size=1800";

const purple = "#632eb9";
const coordConverter = proj4("EPSG:4326", "EPSG:3785");

const curHour = new Date().getHours();
const wrap = x => ((x % 24) + 24) % 24;
const yMin = 0;
const yMax = 110;
const redLimit = 75;

const Home = props => {
  return (
    <div className={css.root}>
      <h1>Hot Spot Indicator</h1>
      <h4>(Prototype)</h4>
      <div className={css.text}>
        <div className={css.textLeft}>
          <p>
            We are using movements around the city — here shown for Nørreport —
            to forecast three scenarios for how crowded the Central Station will
            be during the day. The three scenarios are based on the following
            assumptions about the flow of people at Nørreport.
          </p>
        </div>
        <div className={css.textRight}>
          <ol>
            <li>
              <strong>Optimistic:</strong> The flow of people is{" "}
              <em>25 percent below</em> the same time the previous week.
            </li>
            <li>
              <strong>Likely:</strong> The flow of people is <em>the same</em>{" "}
              as the same time the previous week.
            </li>
            <li>
              <strong>Pessimistic:</strong> The flow of people is{" "}
              <em>25 percent above</em> the same time the previous week.
            </li>
          </ol>
        </div>
      </div>
      <PredictedGraph actual={actual} predictions={predictions} />
      <Map data={csv} />
      <BarCharts data={csv} />
    </div>
  );
};

// Prediction
// ----------------------------------------------------

const scenarioOptions = [
  { value: "Optimistic", label: "Optimistic" },
  { value: "Likely", label: "Likely" },
  { value: "Pessimistic", label: "Pessimistic" }
];

const PredictedGraph = ({ actual, predictions }) => {
  const [hour, setHour] = useState(curHour);
  const [scenario, setScenario] = useState(scenarioOptions[0]);

  // Find the 7 preciding actual hours
  const actualData = { id: "actual", data: [] };
  for (let i = 6; i >= 0; i--) {
    const wrapHour = wrap(hour - i);
    const item = actual.find(a => a.hour === wrapHour);
    actualData.data.push({
      x: hour - i,
      y: item.y
    });
  }

  // Find the predicted data for the next 5 hours in the chosen scenario
  const predictedRows = predictions.filter(p => {
    return p.hour === wrap(hour) && p.scenario === scenario.value;
  });

  // Split into individual lines
  const lastActualDataItem = actualData.data[actualData.data.length - 1];
  const yhatData = { id: "yhat", data: [lastActualDataItem] };
  const yhatLowerData = { id: "yhatLower", data: [lastActualDataItem] };
  const yhatUpperData = { id: "yhatUpper", data: [lastActualDataItem] };
  for (let i = 0; i < predictedRows.length; i++) {
    // We should really parse the hour in the data
    const x = hour + i + 1;
    yhatData.data.push({ x, y: predictedRows[i].yhat });
    yhatLowerData.data.push({ x, y: predictedRows[i].yhat_lower });
    yhatUpperData.data.push({ x, y: predictedRows[i].yhat_upper });
  }

  const xMin = actualData.data[0].x;
  const xMax = actualData.data[actualData.data.length - 1].x + 5;

  return (
    <div className={css.predicted}>
      <div className={css.columns}>
        <div className={css.predictedLeft}>
          <label>Scenario</label>
          <Select
            options={[
              { value: "Optimistic", label: "Optimistic" },
              { value: "Likely", label: "Likely" },
              { value: "Pessimistic", label: "Pessimistic" }
            ]}
            value={scenario}
            onChange={setScenario}
          />
        </div>
        <div className={css.predictedRight}>
          <label>Hour</label>
          <InputRange
            formatLabel={val => wrap(val).toString().padStart(2, "0") + ":00"}
            minValue={curHour - 23}
            maxValue={curHour}
            value={hour}
            onChange={setHour}
          />
        </div>
      </div>
      <div className={css.predictedGraph}>
        <ResponsiveLine
          animate={false}
          data={[actualData, yhatLowerData, yhatUpperData, yhatData]}
          yhatLower={yhatLowerData}
          yhatUpper={yhatUpperData}
          colors={[purple, "#C4AEE9", "#C4AEE9", purple]}
          margin={{ top: 5, right: 15, bottom: 45, left: 50 }}
          tooltip={({ point }) => {
            return (
              <div className={css.tooltip}>
                <label>Hour</label>
                <span>
                  {wrap(point.data.x).toString().padStart(2, 0) + ":00"}
                </span>
                <label>Popularity</label>
                <span>{Math.round(point.data.y)}</span>
              </div>
            );
          }}
          xScale={{
            type: "linear",
            min: xMin,
            max: xMax
          }}
          yScale={{
            type: "linear",
            min: yMin,
            max: yMax
          }}
          xMin={xMin}
          xMax={xMax}
          yMin={yMin}
          yMax={yMax}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            orient: "bottom",
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "Hour",
            legendOffset: 36,
            legendPosition: "middle",
            format: x => wrap(x).toString().padStart(2, "0") + ":00"
          }}
          axisLeft={{
            orient: "left",
            tickSize: 5,
            tickValues: 4,
            tickPadding: 5,
            tickRotation: 0,
            legend: "Popularity",
            legendOffset: -40,
            legendPosition: "middle"
          }}
          crosshairType={"x"}
          pointSize={3}
          pointColor={{ from: "color" }}
          pointBorderWidth={2}
          pointBorderColor={{ from: "serieColor" }}
          pointLabel="y"
          pointLabelYOffset={-12}
          useMesh={true}
          defs={[
            patternLinesDef("background-pattern", {
              spacing: 10,
              rotation: -45,
              lineWidth: 1,
              background: "#FCF8EF",
              color: "#E8DEF9"
            }),
            patternLinesDef("background-pattern-red", {
              spacing: 10,
              rotation: -45,
              lineWidth: 1,
              background: "#fbebea",
              color: "#fbd5d5"
            }),
            patternLinesDef("background-pattern-red-alert", {
              spacing: 10,
              rotation: -45,
              lineWidth: 1,
              background: "#FFE3E1",
              color: "#fbd5d5"
            })
          ]}
          layers={[
            PredictedBackground,
            "grid",
            "markers",
            "areas",
            PredictedText,
            PredictedArea,
            "lines",
            "axes",
            "points",
            "legends",
            "mesh",
            "slices"
          ]}
        />
        {hour === curHour && <Live />}
      </div>
    </div>
  );
};

const PredictedBackground = ({
  series,
  xScale,
  yScale,
  xMin,
  xMax,
  yMin,
  yMax
}) => {
  const lastActualX = series[0].data[series[0].data.length - 1].position.x;
  const hasRedNumbers = series
    .find(s => s.id === "yhatUpper")
    .data.some(item => item.data.y >= redLimit);
  return (
    <g>
      <rect
        fill="url(#background-pattern-red)"
        x={lastActualX}
        y={yScale(yMax)}
        width={xScale(xMax) - lastActualX}
        height={yScale(redLimit) - yScale(yMax)}
      />
      {hasRedNumbers && (
        <rect
          fill="url(#background-pattern-red-alert)"
          x={lastActualX}
          y={yScale(yMax)}
          width={xScale(xMax) - lastActualX}
          height={yScale(redLimit) - yScale(yMax)}
        />
      )}
      <rect
        fill="url(#background-pattern)"
        x={lastActualX}
        y={yScale(redLimit)}
        width={xScale(xMax) - lastActualX}
        height={yScale(yMin) - yScale(redLimit)}
      />
    </g>
  );
};

const PredictedText = ({ series, xScale, yScale, yMax, yMin }) => {
  const lastActualX = series[0].data[series[0].data.length - 1].position.x;
  const hasRedNumbers = series
    .find(s => s.id === "yhatUpper")
    .data.some(item => item.data.y >= redLimit);
  return (
    <g>
      <text
        fill={purple}
        x={lastActualX - 10}
        y={yScale(yMax) + 22}
        textAnchor="end">
        Observed
      </text>
      <text fill={purple} x={lastActualX + 10} y={yScale(yMax) + 22}>
        Predicted
      </text>
      {hasRedNumbers && (
        <text
          fill="#EE9696"
          fontStyle="italic"
          x={lastActualX + 85}
          y={yScale(yMax) + 22}>
          High risk
        </text>
      )}
      <line
        x1={lastActualX}
        y1={yScale(yMax)}
        x2={lastActualX}
        y2={yScale(yMin)}
        stroke={"#9d73e2"}
      />
    </g>
  );
};

const PredictedArea = ({ xScale, yScale, yhatLower, yhatUpper }) => {
  let d = "";
  for (let i = 0; i < yhatLower.data.length; i++) {
    const pos = yhatLower.data[i];
    d +=
      i === 0
        ? `M ${xScale(pos.x)} ${yScale(pos.y)}`
        : `L ${xScale(pos.x)} ${yScale(pos.y)}`;
  }
  for (let i = yhatUpper.data.length - 1; i >= 0; i--) {
    const pos = yhatUpper.data[i];
    d += `L ${xScale(pos.x)} ${yScale(pos.y)}`;
  }
  return <path fill="#E3D2FF" d={d} />;
};

// Map
// ----------------------------------------------------

const Map = ({ data }) => {
  const [hour, setHour] = useState(curHour);

  // const [mapY, mapX] = coordConverter.forward([55.705878, 12.52179]);
  // console.log(mapX, mapY);
  const mapX = 1407100;
  const mapY = 6198820;
  const multX = 0.245;
  const multY = 0.455;

  const { hourPlaces } = useMemo(() => {
    const hourPlaces = [];
    for (let i = 0; i < data.length; i++) {
      if (data[i].hour === wrap(hour)) {
        hourPlaces.push(data[i]);
      }
    }
    return { hourPlaces };
  }, [data, hour]);

  return (
    <div className={css.mapOuter}>
      <h2>Popularity by time</h2>

      <div className={css.mapInput}>
        <label>Hour</label>
        <InputRange
          formatLabel={val => wrap(val).toString().padStart(2, "0") + ":00"}
          minValue={curHour - 23}
          maxValue={curHour}
          value={hour}
          onChange={setHour}
        />
      </div>

      <div className={css.map} style={{ backgroundImage: `url(${map})` }}>
        <svg width="100%" viewBox="0 0 1800 1200">
          {hourPlaces.map((place, i) => {
            const [py, px] = coordConverter.forward([
              place.coord_lat,
              place.coord_long
            ]);
            const rx = (px - mapX) * multX;
            const ry = (mapY - py) * multY;
            return (
              <g key={`${place.name}-${i}`}>
                <circle
                  cx={rx}
                  cy={ry}
                  fill={purple}
                  r={place.current_popularity / 2}
                />
              </g>
            );
          })}
        </svg>
        {hour === curHour && <Live />}
      </div>
    </div>
  );
};

// Lines
// ----------------------------------------------------

const BarCharts = ({ data }) => {
  const { places, placesData, placesOptions } = useMemo(() => {
    // Find all places
    const placeNames = unique(data.map(place => place.name));

    // Make options for places
    const placesOptions = placeNames.map(key => ({ value: key, label: key }));

    // Make empty data points for each place
    const places = [];
    for (let i = 0; i < placeNames.length; i++) {
      const place = { name: placeNames[i], data: [] };
      for (let j = curHour - 23; j <= curHour; j++) {
        place.data.push({
          hour: wrap(j),
          hourStr: wrap(j).toString(),
          popularity: 0
        });
      }
      places.push(place);
    }

    // Fill in data points!
    for (let i = 0; i < data.length; i++) {
      const place = places.find(place => data[i].name === place.name);
      const item = place.data.find(d => d.hour === data[i].hour);
      item.popularity = data[i].current_popularity;
    }

    return { places, placesOptions };
  }, [csv, curHour]);

  const [selected, setSelected] = useState(
    placesOptions.filter(o =>
      [
        "Copenhagen Central Station",
        "Nørreport",
        "Ørsteds Park",
        "Dybbølsbro"
      ].includes(o.value)
    )
  );

  const filteredPlaces = places.filter(place =>
    selected.find(s => s.value === place.name)
  );

  const handleChange = options => {
    setSelected(options);
  };

  return (
    <div className={css.lineGraphs}>
      <h2>Popularity by location</h2>
      <Select
        options={placesOptions}
        value={placesOptions.filter(o => selected.includes(o))}
        isMulti
        className={css.multi}
        onChange={handleChange}
      />
      {filteredPlaces.map(place => {
        return (
          <div className={css.linePlace} key={place.name}>
            <h3>{place.name}</h3>
            <div className={css.lineGraph}>
              <ResponsiveBar
                keys={["popularity"]}
                indexBy="hourStr"
                animate={false}
                data={place.data}
                padding={0.3}
                margin={{ top: 5, right: 5, bottom: 31, left: 50 }}
                minValue={yMin}
                maxValue={yMax}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  orient: "bottom",
                  tickSize: 5,
                  tickPadding: 0,
                  tickRotation: 0,
                  legend: "",
                  legendOffset: 25,
                  legendPosition: "middle",
                  format: x => wrap(x).toString().padStart(2, "0") + ":00"
                }}
                axisLeft={{
                  orient: "left",
                  tickSize: 5,
                  tickValues: 4,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: "Popularity",
                  legendOffset: -40,
                  legendPosition: "middle"
                }}
                gridYValues={4}
                crosshairType={"x"}
                colors={[purple]}
                labelTextColor="#FFFFFF"
                useMesh={true}
                tooltip={({ data }) => {
                  return (
                    <div className={css.tooltip}>
                      <label>Hour</label>
                      <span>{data.hourStr.padStart(2, 0) + ":00"}</span>
                      <label>Popularity</label>
                      <span>{Math.round(data.popularity)}</span>
                    </div>
                  );
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Helpers
// ----------------------------------------------------

const Live = () => (
  <span className={css.live}>
    <Radio />
    <span>Live</span>
  </span>
);

export default Home;
